// src/components/tables/ClientesTable.jsx
import React, { useState } from 'react';
import { FaTrash, FaEdit, FaEnvelope, FaChartBar, FaEye, FaTag, FaShoppingCart, FaClock } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import EditClienteModal from '../modals/EditClienteModal';
import { patch } from '../../helpers/apiHelper';
import Swal from 'sweetalert2';
import RedeemRewardButton from '../rewards/RedeemRewardButton';
import SendRewardButton from '../rewards/SendRewardButton';
import CustomerDetailedMetrics from './CustomerDetailedMetrics';
import { getSegmentColor } from '../../utils/segmentColors';

// Función helper para formatear fechas
const formatDate = (date) => {
    if (!date) {
        return 'N/A';
    }

    const dateObj = new Date(date);

    if (isNaN(dateObj.getTime())) {
        return 'Fecha inválida';
    }

    const formatted = dateObj.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
    return formatted;
};

// Función helper para obtener la última visita
const getLastVisit = (cliente) => {
    if (!cliente.tickets || cliente.tickets.length === 0) {
        return null;
    }

    // Verificar si los tickets son objetos completos o solo IDs
    const firstTicket = cliente.tickets[0];

    // Si los tickets son solo IDs (strings), no podemos obtener la fecha
    if (typeof firstTicket === 'string') {
        return null;
    }

    // Si los tickets son objetos pero no tienen fecha, usar created_at como fallback
    if (firstTicket && typeof firstTicket === 'object') {
        const ticketDate = firstTicket.fecha || firstTicket.fechaCreacion || firstTicket.createdAt || firstTicket.created_at;

        if (!ticketDate) {
            return null;
        }
    }

    // Ordenar tickets por fecha descendente y tomar el más reciente
    const sortedTickets = [...cliente.tickets].sort((a, b) => {
        const dateA = a.fecha || a.fechaCreacion || a.createdAt || a.created_at;
        const dateB = b.fecha || b.fechaCreacion || b.createdAt || b.created_at;
        return new Date(dateB) - new Date(dateA);
    });

    const lastTicket = sortedTickets[0];
    return lastTicket;
};

const ClientesTable = ({ clientes, onDelete, fetchClientes }) => {
    const [editingCliente, setEditingCliente] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedCustomerForMetrics, setSelectedCustomerForMetrics] = useState(null);
    const [showMetricsModal, setShowMetricsModal] = useState(false);
    const navigate = useNavigate();

    const handleEditCliente = (cliente) => {
        setEditingCliente(cliente);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setEditingCliente(null);
        setShowModal(false);
    };

    const handleSaveCliente = async (updatedCliente) => {
        try {
            // Extraemos solo los campos que queremos actualizar
            const {
                nombre,
                apellido,
                email,
                telefono,
                fechaCumpleanos,
                genero,
                geographic_info,
                segments
            } = updatedCliente;

            const payload = {
                nombre,
                apellido,
                email,
                telefono,
                fechaCumpleanos,
                genero,
                geographic_info,
                segments
            };

            await patch(`clientes/${editingCliente._id}`, payload);
            fetchClientes();
            handleCloseModal();
            Swal.fire({
                title: 'Éxito',
                text: 'Cliente actualizado correctamente',
                icon: 'success',
                confirmButtonText: 'Aceptar',
            });
        } catch (error) {
            Swal.fire({
                title: 'Error',
                text: error.message,
                icon: 'error',
                confirmButtonText: 'Aceptar',
            });
        }
    };

    const handleResendTicket = async (clienteId) => {
        try {
            const response = await patch(`tickets/cliente/${clienteId}/resend`);
            Swal.fire({
                title: 'Éxito',
                text: response.message || 'Ticket reenviado correctamente',
                icon: 'success',
                confirmButtonText: 'Aceptar',
            });
        } catch (error) {
            Swal.fire({
                title: 'Error',
                text: error.message,
                icon: 'error',
                confirmButtonText: 'Aceptar',
            });
        }
    };

    const handleViewStatistics = (clienteId) => {
        navigate(`/statistics/cliente/${clienteId}`);
    };

    const handleViewDetailedMetrics = (cliente) => {
        setSelectedCustomerForMetrics(cliente);
        setShowMetricsModal(true);
    };

    const closeMetricsModal = () => {
        setSelectedCustomerForMetrics(null);
        setShowMetricsModal(false);
    };


    return (
        <div className="w-full">
            {/* 
              ─────────────────────────────────────────────────
              VERSIÓN DE ESCRITORIO (MD+): TABLA CLÁSICA
              ─────────────────────────────────────────────────
            */}
            <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full w-full table-auto">
                    <thead>
                        <tr className="bg-gray-100">
                            <th className="px-4 py-2 text-left">Nombre</th>
                            <th className="px-4 py-2 text-left">Apellido</th>
                            <th className="px-4 py-2 text-left">Email</th>
                            <th className="px-4 py-2 text-left">Teléfono</th>
                            <th className="px-4 py-2 text-left">Cumpleaños</th>
                            <th className="px-4 py-2 text-left">Última Visita</th>
                            <th className="px-4 py-2 text-left">Segmentos</th>
                            <th className="px-4 py-2 text-left">RFM</th>
                            <th className="px-4 py-2 text-left">Métricas</th>
                            <th className="px-4 py-2 text-left">Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {clientes.map((cliente) => (
                            <tr key={cliente._id} className="border-t border-gray-300">
                                <td className="px-4 py-2">{cliente.nombre}</td>
                                <td className="px-4 py-2">{cliente.apellido}</td>
                                <td className="px-4 py-2">{cliente.email}</td>
                                <td className="px-4 py-2">{cliente.telefono || 'N/A'}</td>
                                <td className="px-4 py-2">
                                    {cliente.fechaCumpleanos
                                        ? new Date(cliente.fechaCumpleanos).toLocaleDateString()
                                        : 'N/A'}
                                </td>
                                <td className="px-4 py-2">
                                    {(() => {
                                        const lastVisit = getLastVisit(cliente);
                                        if (!lastVisit) {
                                            return <span className="text-gray-500 text-xs">Sin visitas</span>;
                                        }

                                        // Obtener la fecha del ticket (puede estar en diferentes propiedades)
                                        const visitDateString = lastVisit.fecha || lastVisit.fechaCreacion || lastVisit.createdAt || lastVisit.created_at;

                                        if (!visitDateString) {
                                            return (
                                                <div>
                                                    <div className="text-xs text-orange-500">
                                                        Sin fecha
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        Ticket sin fecha
                                                    </div>
                                                </div>
                                            );
                                        }

                                        const visitDate = new Date(visitDateString);
                                        const now = new Date();

                                        if (isNaN(visitDate.getTime())) {
                                            return (
                                                <div>
                                                    <div className="text-xs text-red-500">
                                                        Fecha inválida
                                                    </div>
                                                    <div className="text-xs text-gray-500">
                                                        Revisar datos
                                                    </div>
                                                </div>
                                            );
                                        }

                                        const diffTime = Math.abs(now - visitDate);
                                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                                        let colorClass = 'text-gray-600';
                                        if (diffDays <= 7) {
                                            colorClass = 'text-green-600 font-medium';
                                        } else if (diffDays <= 30) {
                                            colorClass = 'text-yellow-600';
                                        } else {
                                            colorClass = 'text-red-600';
                                        }

                                        return (
                                            <div>
                                                <div className={`text-xs ${colorClass}`}>
                                                    {formatDate(visitDateString)}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    {diffDays === 1 ? 'Ayer' :
                                                        diffDays < 7 ? `Hace ${diffDays} días` :
                                                            diffDays < 30 ? `Hace ${Math.floor(diffDays / 7)} semanas` :
                                                                `Hace ${Math.floor(diffDays / 30)} meses`}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </td>
                                <td className="px-4 py-2">
                                    {cliente.segments && cliente.segments.length > 0 ? (
                                        cliente.segments.map((seg, index) => (
                                            <span
                                                key={index}
                                                className={`inline-block text-xs px-2 py-1 rounded mr-1 mb-1 ${getSegmentColor(seg.label || seg.value)}`}
                                            >
                                                {seg.label || seg.value}
                                            </span>
                                        ))
                                    ) : (
                                        <span className="text-gray-500 text-xs">Sin segmentos</span>
                                    )}
                                </td>
                                <td className="px-4 py-2">
                                    {cliente.rfmScore !== undefined && cliente.rfmScore !== null ? (
                                        <div className="text-center">
                                            <div className="font-bold text-lg text-blue-600">{cliente.rfmScore}</div>
                                            <div className="text-xs text-gray-500">{cliente.segment || 'N/A'}</div>
                                        </div>
                                    ) : (
                                        <div className="text-center">
                                            <div className="text-xs text-gray-400">Sin datos RFM</div>
                                            <div className="text-xs text-gray-400">disponibles</div>
                                        </div>
                                    )}
                                </td>
                                <td className="px-4 py-2">
                                    <div className="space-y-1">
                                        {cliente.avg_basket_size !== undefined && cliente.avg_basket_size !== null ? (
                                            <div className="flex items-center text-xs text-gray-600">
                                                <FaShoppingCart className="mr-1 text-green-500" />
                                                <span className="font-medium">${cliente.avg_basket_size.toFixed(2)}</span>
                                            </div>
                                        ) : (
                                            <div className="text-xs text-gray-400">Sin ticket</div>
                                        )}
                                        {cliente.frequency !== undefined && cliente.frequency !== null ? (
                                            <div className="flex items-center text-xs text-gray-600">
                                                <FaClock className="mr-1 text-blue-500" />
                                                <span className="font-medium">{cliente.frequency} {cliente.frequency === 1 ? 'compra' : 'compras'}</span>
                                            </div>
                                        ) : (
                                            <div className="text-xs text-gray-400">Sin frecuencia</div>
                                        )}
                                    </div>
                                </td>
                                <td className="px-4 py-2 flex space-x-1">
                                    <button
                                        onClick={() => handleEditCliente(cliente)}
                                        className="bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-700 text-xs"
                                        title="Editar cliente"
                                    >
                                        <FaEdit />
                                    </button>
                                    <button
                                        onClick={() => handleViewDetailedMetrics(cliente)}
                                        className="bg-purple-500 text-white px-2 py-1 rounded hover:bg-purple-700 text-xs"
                                        title="Ver métricas detalladas"
                                    >
                                        <FaEye />
                                    </button>
                                    <button
                                        onClick={() => handleViewStatistics(cliente._id)}
                                        className="bg-indigo-500 text-white px-2 py-1 rounded hover:bg-indigo-700 text-xs"
                                        title="Ver estadísticas"
                                    >
                                        <FaChartBar />
                                    </button>
                                    <button
                                        onClick={() => handleResendTicket(cliente._id)}
                                        className="bg-green-500 text-white px-2 py-1 rounded hover:bg-green-700 text-xs"
                                        title="Reenviar ticket"
                                    >
                                        <FaEnvelope />
                                    </button>
                                    <SendRewardButton clienteId={cliente._id} />
                                    <RedeemRewardButton
                                        clienteId={cliente._id}
                                        recompensaId={cliente.recompensaId}
                                    />
                                    <button
                                        onClick={() => onDelete(cliente._id)}
                                        className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-700 text-xs"
                                        title="Eliminar cliente"
                                    >
                                        <FaTrash />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* 
              ─────────────────────────────────────────────────
              VERSIÓN MÓVIL (POR DEBAJO DE MD): TARJETAS SIMPLIFICADAS
              ─────────────────────────────────────────────────
            */}
            <div className="md:hidden space-y-4 px-4 py-2">
                {clientes.map((cliente) => (
                    <div
                        key={cliente._id}
                        className="bg-white border border-gray-300 rounded-lg shadow-sm p-4"
                    >
                        {/* Datos principales del cliente */}
                        <div className="mb-3">
                            <h2 className="text-lg font-bold truncate text-gray-800 mb-1">
                                {cliente.nombre} {cliente.apellido}
                            </h2>
                            <p className="text-sm text-gray-700">
                                <span className="font-semibold">Email:</span> {cliente.email}
                            </p>
                            <p className="text-sm text-gray-700">
                                <span className="font-semibold">Teléfono:</span>{' '}
                                {cliente.telefono || 'N/A'}
                            </p>
                            <p className="text-sm text-gray-700">
                                <span className="font-semibold">Cumpleaños:</span>{' '}
                                {cliente.fechaCumpleanos
                                    ? new Date(cliente.fechaCumpleanos).toLocaleDateString()
                                    : 'N/A'}
                            </p>
                            <p className="text-sm text-gray-700">
                                <span className="font-semibold">Última Visita:</span>{' '}
                                {(() => {
                                    const lastVisit = getLastVisit(cliente);
                                    if (!lastVisit) {
                                        return <span className="text-gray-500">Sin visitas</span>;
                                    }

                                    // Obtener la fecha del ticket (puede estar en diferentes propiedades)
                                    const visitDateString = lastVisit.fecha || lastVisit.fechaCreacion || lastVisit.createdAt || lastVisit.created_at;

                                    if (!visitDateString) {
                                        return <span className="text-orange-500">Sin fecha</span>;
                                    }

                                    const visitDate = new Date(visitDateString);
                                    const now = new Date();

                                    if (isNaN(visitDate.getTime())) {
                                        return <span className="text-red-500">Fecha inválida</span>;
                                    }

                                    const diffTime = Math.abs(now - visitDate);
                                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                                    let colorClass = 'text-gray-600';
                                    if (diffDays <= 7) {
                                        colorClass = 'text-green-600 font-medium';
                                    } else if (diffDays <= 30) {
                                        colorClass = 'text-yellow-600';
                                    } else {
                                        colorClass = 'text-red-600';
                                    }

                                    return (
                                        <span className={colorClass}>
                                            {formatDate(visitDateString)} ({diffDays === 1 ? 'Ayer' :
                                                diffDays < 7 ? `Hace ${diffDays} días` :
                                                    diffDays < 30 ? `Hace ${Math.floor(diffDays / 7)} semanas` :
                                                        `Hace ${Math.floor(diffDays / 30)} meses`})
                                        </span>
                                    );
                                })()}
                            </p>
                            {/* Etiquetas (segments) */}
                            <div className="mt-2 flex flex-wrap gap-2">
                                {cliente.segments && cliente.segments.length > 0 ? (
                                    cliente.segments.map((seg, index) => (
                                        <span
                                            key={index}
                                            className={`text-xs px-2 py-1 rounded ${getSegmentColor(seg.label || seg.value)}`}
                                        >
                                            {seg.label || seg.value}
                                        </span>
                                    ))
                                ) : (
                                    <span className="text-gray-500 text-xs">Sin segmentos</span>
                                )}
                            </div>

                            {/* Información RFM y Métricas */}
                            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                                <div className="bg-gray-50 p-2 rounded">
                                    <div className="font-semibold text-gray-700">RFM</div>
                                    {cliente.rfmScore !== undefined && cliente.rfmScore !== null ? (
                                        <div className="text-blue-600 font-bold">{cliente.rfmScore}</div>
                                    ) : (
                                        <div className="text-gray-400">Sin datos</div>
                                    )}
                                </div>
                                <div className="bg-gray-50 p-2 rounded">
                                    <div className="font-semibold text-gray-700">Ticket</div>
                                    {cliente.avg_basket_size !== undefined && cliente.avg_basket_size !== null ? (
                                        <div className="text-green-600 font-bold">${cliente.avg_basket_size.toFixed(2)}</div>
                                    ) : (
                                        <div className="text-gray-400">Sin datos</div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* 
                          BOTONES MÓVIL:
                          En este ejemplo se reducen las opciones para no abrumar.
                          Dejamos sólo Editar y Eliminar. 
                          Si quieres más, quítales la clase hidden o agrégalos aquí directamente.
                        */}
                        <div className="grid grid-cols-2 gap-2">
                            {/* Botón Editar */}
                            <button
                                onClick={() => handleEditCliente(cliente)}
                                className="flex items-center justify-center bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition-colors"
                            >
                                <FaEdit className="mr-1" />
                                <span>Editar</span>
                            </button>

                            {/* Botón Eliminar */}
                            <button
                                onClick={() => onDelete(cliente._id)}
                                className="flex items-center justify-center bg-red-600 text-white py-2 rounded hover:bg-red-700 transition-colors"
                            >
                                <FaTrash className="mr-1" />
                                <span>Eliminar</span>
                            </button>
                        </div>
                        <div className="mt-2 flex flex-col gap-2">
                            <button
                                onClick={() => handleViewDetailedMetrics(cliente)}
                                className="w-full flex items-center justify-center bg-purple-600 text-white py-2 rounded hover:bg-purple-700 transition-colors"
                            >
                                <FaEye className="mr-1" />
                                <span>Métricas Detalladas</span>
                            </button>
                            <button
                                onClick={() => handleResendTicket(cliente._id)}
                                className="w-full flex items-center justify-center bg-green-600 text-white py-2 rounded hover:bg-green-700 transition-colors"
                            >
                                <FaEnvelope className="mr-1" />
                                <span>Reenviar</span>
                            </button>
                            <button
                                onClick={() => handleViewStatistics(cliente._id)}
                                className="w-full flex items-center justify-center bg-indigo-500 text-white py-2 rounded hover:bg-indigo-700 transition-colors"
                            >
                                <FaChartBar className="mr-1" />
                                <span>Estadísticas</span>
                            </button>
                        </div>
                        <div className="mt-2">
                            <SendRewardButton clienteId={cliente._id} />
                        </div>
                        <div className="mt-2">
                            <RedeemRewardButton
                                clienteId={cliente._id}
                                recompensaId={cliente.recompensaId}
                            />
                        </div>

                    </div>
                ))}
            </div>

            {/* Modal para edición de cliente */}
            {showModal && editingCliente && (
                <EditClienteModal
                    cliente={editingCliente}
                    onClose={handleCloseModal}
                    onSave={handleSaveCliente}
                />
            )}

            {/* Modal para métricas detalladas */}
            {showMetricsModal && selectedCustomerForMetrics && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-4 border-b flex justify-between items-center">
                            <h2 className="text-xl font-bold">
                                Métricas Detalladas - {selectedCustomerForMetrics.nombre} {selectedCustomerForMetrics.apellido}
                            </h2>
                            <button
                                onClick={closeMetricsModal}
                                className="text-gray-500 hover:text-gray-700 text-2xl"
                            >
                                ✕
                            </button>
                        </div>
                        <div className="p-4">
                            <CustomerDetailedMetrics
                                customerId={selectedCustomerForMetrics._id}
                                period="12"
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ClientesTable;
